import * as React from 'react';
import moment from 'moment-timezone';

import { CheckboxVisibility, IColumn, Link, Selection, SelectionMode } from '@fluentui/react';

import { ROUTES } from '../../config/routes';
import { ShimmeredDetailsList } from '@fluentui/react/lib/ShimmeredDetailsList';
import { Visit } from '../../redux/visits/types';
import { VisitStatusIcon } from '../visit/VisitStatusIcon';
import { VisitTag } from '../visit/VisitTag';
import { friendlyAge } from '../../util/Date';
import { AsyncData } from '../../redux/core';
import { VisitStatus } from '../../types';
import { Market } from '../../api/markets.types';

export interface VisitListProps {
  isFetching: boolean;
  visits: Visit[];
  columns: string[];
  markets: AsyncData<Market[]>;
  selectedVisitCode?: string;
  onSelected?: (visitCode: string) => void;
}

export interface VisitListState {
  columns: IColumn[];
}

export class VisitList extends React.Component<VisitListProps, VisitListState> {
  private selection: Selection;

  constructor(props: VisitListProps) {
    super(props);
    this.state = {
      columns: this.buildColumns(props.columns),
    };

    this.selection = new Selection({
      getKey: (data: any) => data.visitCode,
      canSelectItem: () => true,
    });

    this.selection.setItems(props.visits as any);
    if (props.selectedVisitCode) {
      this.selection.setKeySelected(props.selectedVisitCode, true, true);
    }
  }

  public componentDidUpdate(prevProps: VisitListProps) {
    if (this.props.visits !== prevProps.visits) {
      this.selection.setItems(this.props.visits as any);
      if (this.props.selectedVisitCode) {
        this.selection.setKeySelected(this.props.selectedVisitCode, true, true);
      }
    }
    if (this.props.selectedVisitCode !== prevProps.selectedVisitCode) {
      if (this.props.selectedVisitCode && this.selection.isKeySelected(this.props.selectedVisitCode)) {
        this.selection.toggleKeySelected(this.props.selectedVisitCode);
      }
      if (this.props.selectedVisitCode) {
        this.selection.setKeySelected(this.props.selectedVisitCode, true, true);
      }
    }
  }

  public render(): JSX.Element {
    return (
      <ShimmeredDetailsList
        checkboxVisibility={CheckboxVisibility.hidden}
        compact={true}
        columns={this.state.columns}
        items={this.props.visits}
        enableShimmer={this.props.isFetching}
        shimmerLines={8}
        selectionMode={SelectionMode.single}
        selection={this.selection}
        setKey="does_not_change"
        onActiveItemChanged={this.onActiveItemChanged}
      />
    );
  }

  private onActiveItemChanged = (item: Visit) => {
    if (this.props.onSelected) {
      this.props.onSelected(item.visitCode);
    }
  };

  private buildColumns(columns: string[]): IColumn[] {
    return columns.map((key) => this.getColumnRenderer(key));
  }

  private getMarketTimeZone(visit: Visit): string {
    let marketTimeZone = '';
    if (this.props.markets && this.props.markets.data) {
      const market = this.props.markets.data.find((m) => m.code === visit.marketCode);
      if (market) {
        marketTimeZone = market.timeZone;
      }
    }
    return marketTimeZone || moment.tz.guess();
  }

  private getColumnRenderer(field: string): IColumn {
    switch (field) {
      case 'code':
        return {
          key: field,
          name: 'Visit Code',
          minWidth: 70,
          maxWidth: 70,
          onRender: (item: Visit) => {
            return <Link href={`${ROUTES.visits}/${item.visitCode}`}>{item.visitCode}</Link>;
          },
        };

      case 'status':
        return {
          key: field,
          name: 'Status',
          minWidth: 70,
          maxWidth: 70,
          isMultiline: true,
          onRender: (item: Visit) => {
            return <VisitStatusIcon status={item.status || item.visitStatus} />;
          },
        };

      case 'insurance':
        return {
          key: field,
          name: '',
          minWidth: 60,
          maxWidth: 60,
          onRender: (item: Visit) => {
            return <VisitTag visit={item} />;
          },
        };

      case 'service':
        return {
          key: field,
          name: 'Service',
          minWidth: 80,
          maxWidth: 80,
          isMultiline: true,
          onRender: (item: Visit) => {
            return item.visitServiceShortName || item.visitServiceName || item.visitServiceDescription;
          },
        };

      case 'patient':
        return {
          key: field,
          name: 'Patient',
          minWidth: 80,
          maxWidth: 140,
          isMultiline: true,
          isResizable: true,
          onRender: (item: Visit) => {
            return (
              <div>
                <Link href={`${ROUTES.patients}/${item.patientId}`}>{item.buildPatientFullName || item.patientName}</Link>
                <div style={{ fontWeight: 'bold' }}>{friendlyAge(item.patientDob)}</div>
                <div>{item.patientDob}</div>
              </div>
            );
          },
        };

      case 'address':
        return {
          key: field,
          name: 'Address',
          fieldName: 'addressLong',
          minWidth: 80,
          maxWidth: 240,
          isMultiline: true,
          isResizable: true,
        };

      case 'doctor':
        return {
          key: field,
          name: 'Provider',
          minWidth: 80,
          maxWidth: 140,
          isMultiline: true,
          onRender: (item: Visit) => {
            return <Link href={`${ROUTES.providers}/${item.doctorId}`}>{item.buildDoctorFullName || item.doctorName}</Link>;
          },
        };

      case 'assistant':
        return {
          key: field,
          name: 'Assistant',
          minWidth: 80,
          maxWidth: 140,
          isMultiline: true,
          onRender: (item: Visit) => {
            return <Link href={`${ROUTES.providers}/${item.assistantId}`}>{item.buildAssistantFullName || item.assistantName}</Link>;
          },
        };

      case 'date':
        return {
          key: field,
          name: 'Date',
          fieldName: 'longDateString',
          minWidth: 80,
          maxWidth: 80,
        };

      case 'timeslot':
        return {
          key: field,
          name: 'Timeslot',
          fieldName: 'longTimeString',
          minWidth: 80,
          maxWidth: 80,
          isMultiline: true,
        };

      case 'scheduled-start':
        return {
          key: field,
          name: 'Start',
          minWidth: 120,
          maxWidth: 120,
          onRender: (item: Visit) => {
            if (item.timeStartedVisit) {
              return moment(item.timeStartedVisit, 'YYYY-MM-DDTHH:mm:ss').tz(this.getMarketTimeZone(item)).format('h:mm A');
            } else {
              return moment(item.scheduledTime, 'YYYY-MM-DDTHH:mm:ss').tz(this.getMarketTimeZone(item)).format('~h:mm A');
            }
          },
        };

      case 'eta':
        return {
          key: field,
          name: 'Predicted ETA',
          minWidth: 120,
          maxWidth: 120,
          onRender: (item: Visit) => {
            if (item.recommendedTime) {
              return moment(item.recommendedTime, 'YYYY-MM-DDTHH:mm:ss').tz(this.getMarketTimeZone(item)).format('h:mm A');
            } else {
              return "None";
            }
          },
        };

      case 'duration':
        return {
          key: field,
          name: 'Duration',
          minWidth: 60,
          maxWidth: 60,
          onRender: (item: Visit) => {
            if (!item.timeStartedVisit || (!item.timeEndedVisit && item.status !== VisitStatus.STARTED)) {
              return null;
            }

            const mtzStart = moment(item.timeStartedVisit, 'YYYY-MM-DDTHH:mm:ss').tz(this.getMarketTimeZone(item));
            const mtzEnd = item.timeEndedVisit ? moment(item.timeEndedVisit, 'YYYY-MM-DDTHH:mm:ss').tz(this.getMarketTimeZone(item)) : moment.tz(this.getMarketTimeZone(item));
            return <div style={{ textAlign: 'center' }}>{`${moment.duration(mtzEnd.diff(mtzStart)).asMinutes().toFixed(0)} mins`}</div>;
          },
        };

      default:
        return {
          key: field,
          name: field,
          fieldName: field,
          minWidth: 100,
          maxWidth: 100,
          isResizable: true,
        };
    }
  }
}
